import { useState } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import type { CitationSnapshot } from '@/types/citation'

interface RemovedCitationsSectionProps {
  /** Map<claimUuid, CitationSnapshot>. Session-scoped — this list is
   *  empty after a page reload. Cross-session recovery is by re-audit. */
  removedCitations: Map<string, CitationSnapshot>
  /** Resolves a claim_uuid (= snapshot.claimUuid) to a human label for the
   *  card. Typically the corresponding claim's text or sentence excerpt. */
  getClaimLabel: (claimUuid: string) => string
  /** Resolves a source_uuid to a human-readable citation label. */
  getSourceLabel: (sourceUuid: string) => string
  onRestore: (claimUuid: string) => void
}

const mdComponents: Components = {
  p: ({ children }) => <>{children}</>,
  em: ({ children }) => <i>{children}</i>,
}

function parseUuids(json: string): string[] {
  try {
    const parsed = JSON.parse(json)
    if (Array.isArray(parsed)) return parsed.filter((u): u is string => typeof u === 'string')
  } catch { /* empty */ }
  return []
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * D11/D12: Session-scoped collapsible section listing citations the writer
 * removed in this session. Mirrors HiddenSourcesSection layout. Each entry
 * has a Restore button that re-inserts the citation node at its original
 * position (best-effort; ⌘Z is the more reliable in-session recovery).
 */
export function RemovedCitationsSection({
  removedCitations,
  getClaimLabel,
  getSourceLabel,
  onRestore,
}: RemovedCitationsSectionProps) {
  const [isOpen, setIsOpen] = useState(false)

  const entries = Array.from(removedCitations.entries())
  if (entries.length === 0) return null

  return (
    <div className="mt-0.5">
      <button
        type="button"
        className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer flex items-center gap-1.5 py-1.5 w-full hover:text-foreground transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-[10px] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
          ›
        </span>
        Removed citations ({entries.length})
      </button>

      {isOpen && (
        <div className="space-y-1.5">
          {entries.map(([claimUuid, snap]) => {
            const sourceUuids = parseUuids(snap.sourceUuids)
            const sourceList = sourceUuids.map(getSourceLabel).join('; ')
            return (
              <div
                key={claimUuid}
                className="bg-muted rounded-lg opacity-55 overflow-hidden"
              >
                <div className="flex items-start gap-1.5 px-2.5 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Removed from text · {formatTime(snap.removedAt)}
                    </div>
                    <div className="text-[11px] font-medium text-foreground leading-tight">
                      <ReactMarkdown components={mdComponents}>{getClaimLabel(claimUuid)}</ReactMarkdown>
                    </div>
                    {sourceList && (
                      <div className="text-[10px] text-muted-foreground truncate mt-0.5 leading-tight">
                        {sourceList}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-[10px] px-2 py-0.5 rounded-md text-primary hover:bg-primary/10 shrink-0 transition-colors"
                    onClick={() => onRestore(claimUuid)}
                  >
                    Restore
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
